<template>
  <div class="flex flex-col h-full">
    <DashboardHeader title="Companies" link="/companies" />

    <div class="flex flex-col gap-6 overflow-y-auto">
      <section class="bg-white rounded-lg p-4">
        <div class="flex items-center gap-1 mb-3 text-gray-800">
          <AlignEndHorizontal class="w-4 h-4" />
          <h2 class="text-lg">Top Companies</h2>
        </div>

        <div
          v-if="store.isLoadingShowcase"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <div v-for="n in 3" :key="n" class="h-28 rounded-xl bg-gray-100 animate-pulse"></div>
        </div>
        <div
          v-else-if="store.highlightedCompanies.length"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <HighlightedCompanyCard
            v-for="company in store.highlightedCompanies"
            :key="company.id"
            :company="company"
            :show-edit="isAdmin"
            @click="navigateToCompanyJobs"
            @edit="openEditModal"
          />
        </div>
        <div v-else class="text-sm text-gray-500">No top tier companies yet.</div>
      </section>

      <section
        v-if="store.isLoadingShowcase || store.featuredCompanies.length"
        class="bg-lime-200/50 rounded-lg p-4"
      >
        <div class="flex items-center gap-1 mb-3 text-gray-800">
          <Star class="w-4 h-4" />
          <h2 class="text-lg text-gray-900">Featured Companies</h2>
        </div>

        <div
          v-if="store.isLoadingShowcase"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <div v-for="n in 3" :key="n" class="h-24 rounded-lg bg-gray-100 animate-pulse"></div>
        </div>
        <div
          v-else-if="store.featuredCompanies.length"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <FeaturedCompanyCard
            v-for="company in store.featuredCompanies"
            :key="company.id"
            :company="company"
            :show-edit="isAdmin"
            @edit="openEditModal"
          />
        </div>
      </section>

      <section class="p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-1">
            <HeartHandshake class="w-4 h-4" />
            <h2 class="text-lg text-gray-900">Companies with referring contacts</h2>
          </div>
          <span v-if="store.totalCount && isGuide" class="text-xs text-gray-500">
            {{ store.totalCount }} companies
          </span>
        </div>

        <CompaniesTable
          :companies="store.regularCompanies"
          :loading="store.isLoadingRegular"
          :ordering="currentOrdering"
          :show-edit="isAdmin"
          @sort="handleSort"
          @edit="openEditModal"
        />

        <PaginationComponent
          v-if="store.totalPages > 1"
          :current-page="store.currentPage"
          :total-pages="store.totalPages"
          :use-router="true"
        />
      </section>
    </div>
  </div>

  <CompanyEditModal
    :is-open="isEditOpen"
    :company="editingCompany"
    @update:is-open="handleEditOpenChange"
    @save="handleSave"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useReferringCompaniesStore } from '@/stores/referringCompanies'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import HighlightedCompanyCard from '@/components/companies/HighlightedCompanyCard.vue'
import FeaturedCompanyCard from '@/components/companies/FeaturedCompanyCard.vue'
import CompaniesTable from '@/components/companies/CompaniesTable.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { AlignEndHorizontal, Star, HeartHandshake } from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'
import CompanyEditModal from '@/components/companies/CompanyEditModal.vue'
import type { ReferringCompany } from '@/types/company'

const { isGuide, isAdmin } = useAuth()
const route = useRoute()
const router = useRouter()
const store = useReferringCompaniesStore()
const currentOrdering = computed(() => (route.query.ordering as string) || '')
const editingCompany = ref<ReferringCompany | null>(null)
const isEditOpen = ref(false)

const navigateToCompanyJobs = (companyId: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    path: '/jobs',
    query: {
      status: 'true',
      employer_ids: companyId.toString(),
      page: '1'
    }
  })
}

const fetchRegularCompaniesForRoute = async () => {
  const rawPage = route.query.page ? parseInt(route.query.page as string) : 1
  const page = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage
  await store.fetchRegularCompanies(page, currentOrdering.value || undefined)
}

onMounted(async () => {
  await Promise.all([store.fetchShowcaseCompanies(), fetchRegularCompaniesForRoute()])
})

watch(
  () => [route.query.page, route.query.ordering],
  async ([page, ordering], [oldPage, oldOrdering]) => {
    if (page === oldPage && ordering === oldOrdering) return
    await fetchRegularCompaniesForRoute()
    window.scrollTo(0, 0)
  }
)

const handleSort = async (field: string) => {
  const current = currentOrdering.value
  const nextOrdering = current === `-${field}` ? field : `-${field}`
  await router.push({
    query: {
      ...route.query,
      ordering: nextOrdering,
      page: '1'
    }
  })
}

const openEditModal = (company: ReferringCompany) => {
  if (!isAdmin.value) return
  editingCompany.value = company
  isEditOpen.value = true
}

const handleEditOpenChange = (value: boolean) => {
  isEditOpen.value = value
  if (!value) {
    editingCompany.value = null
  }
}

const handleSave = async (payload: {
  is_featured: boolean
  featured_reason: string | null
  job_board_url: string | null
  job_application_notes: string | null
}) => {
  if (!editingCompany.value) return
  await store.updateCompany(editingCompany.value.id, payload)
  await Promise.all([store.fetchShowcaseCompanies(), fetchRegularCompaniesForRoute()])
  handleEditOpenChange(false)
}
</script>
